/**
 * The one place that talks to the API.
 *
 * Session is an httpOnly cookie the browser sends on its own — deliberately
 * not a token in localStorage, which any injected script can read. Writes
 * carry the CSRF token from the readable companion cookie, which is what makes
 * a cross-site POST fail even though the browser attached the session.
 */

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
  /** The session has gone: the caller should send the user to sign in again. */
  get isUnauthenticated() { return this.status === 401; }
  /** Signed in, but this role may not do it — a different message entirely. */
  get isForbidden() { return this.status === 403; }
}

function csrf(): string {
  const value = document.cookie.match(/(?:^|;\s*)tvx_csrf=([^;]+)/)?.[1];
  return value ? decodeURIComponent(value) : '';
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { 'X-Requested-With': 'XMLHttpRequest' };
  const isForm = opts.body instanceof FormData;
  if (opts.body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') headers['X-CSRF-Token'] = csrf();

  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers,
    signal: opts.signal,
    body: opts.body === undefined ? undefined
      : isForm ? (opts.body as FormData) : JSON.stringify(opts.body),
  });

  /* 204 and empty bodies are normal for DELETE; asking for JSON there throws
     on a response that was perfectly fine. */
  const text = await res.text();
  let data: unknown = null;
  if (text) { try { data = JSON.parse(text); } catch { data = text; } }

  if (!res.ok) {
    const message = (data && typeof data === 'object' && 'error' in data
      ? String((data as { error: unknown }).error)
      : null) ?? `Request failed (${res.status})`;
    throw new ApiError(message, res.status, data);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
